/**
 * Precios de lista de referencia por producto, usados para valorar las ventas
 * en $ en la sección de Ventas del reporte de fidelización.
 *
 * El backend expone `monto` por producto (importe leído por OCR de la factura),
 * pero para algunos clientes no refleja el valor de venta a precio de lista. Por
 * eso, en la vista "$" el total se calcula como `precio_lista × cajas
 * equivalentes` por producto, con precios definidos manualmente aquí.
 *
 * Hay una tabla de precios por reporte que usa valoración local:
 *  - Medifarma Ecuador (id 5, $) y Medifarma Perú (id 7, S/): tablas distintas
 *    (productos y moneda distintos). En ambas, presentaciones de la misma marca
 *    se PROMEDIAN (p.ej. IOPAMED 300/100 y 370/100; DOLORAL y DOLORAL FORTE).
 *  - Grupo Farma Ecuador (id 3): precios PVF de la "LISTA DE PRECIOS 01/07/2024"
 *    (GFE). Aquí las presentaciones NO se promedian: cada presentación tiene su
 *    propio precio (p.ej. FEMEN FORTE x10 vs x50), por lo que las `base` son
 *    específicas por presentación.
 *
 * `mapaPreciosReporte(config)` resuelve qué tabla usar según el reporte; devuelve
 * `null` para los reportes que usan el `monto` del backend directamente.
 */

import { claveCadena } from './cadenas'

interface PrecioBase {
  base: string
  precio: number
}

/**
 * Construye el mapa `base normalizada → precio` a partir de una lista de
 * presentaciones. Bases repetidas se promedian (regla de Medifarma). El orden de
 * inserción se preserva: colocar las bases más específicas primero para que el
 * match por prefijo (`startsWith`) no las "capture" una base más corta.
 */
function construirMapaPrecios(entries: PrecioBase[]): Map<string, number> {
  const acc = new Map<string, { sum: number; n: number }>()
  for (const { base, precio } of entries) {
    const k = claveCadena(base)
    const prev = acc.get(k) ?? { sum: 0, n: 0 }
    acc.set(k, { sum: prev.sum + precio, n: prev.n + 1 })
  }
  return new Map(Array.from(acc, ([k, { sum, n }]) => [k, sum / n]))
}

// --- Medifarma (id 5, 7) ---
// Precios de lista por presentación. Se promedian por `base` (misma marca,
// distinta presentación) según instrucción del cliente.
const PRECIOS_MEDIFARMA: PrecioBase[] = [
  { base: 'iopamed', precio: 34.98 },
  { base: 'iopamed', precio: 36.66 },
  { base: 'enoxaparina', precio: 7.32 },
  { base: 'enoxaparina', precio: 8.57 },
  { base: 'clindess', precio: 14.77 },
  { base: 'next day', precio: 5.59 },
  { base: 'soluna', precio: 3.31 },
]

// --- Medifarma Perú (id 7) ---
// Fuente: "precios-medifarma-peru.xlsx", hoja "Especificación", columna
// "P. Vta F" (precio de venta a farmacia por caja). `base` = marca (col A).
// Presentaciones de la misma marca se PROMEDIAN (regla Medifarma): DOLORAL
// aparece dos veces (9.63 y 17.22 → 13.425). Moneda: soles (S/).
const PRECIOS_MEDIFARMA_PERU: PrecioBase[] = [
  { base: 'electrolight', precio: 34.92 },
  { base: 'leche de magnesia', precio: 8.24 },
  { base: 'menthobioticos', precio: 78.22 },
  { base: 'ibumejoral mujer', precio: 64.06 },
  { base: 'nutriben crecimiento', precio: 61.89 },
  { base: 'nutriben confort', precio: 112.88 },
  { base: 'zaidman emulsion', precio: 16.85 },
  { base: 'nistazinc', precio: 18.85 },
  { base: 'pvm adulto vainilla', precio: 43.61 },
  { base: 'pvm junior vainilla', precio: 38.65 },
  { base: 'notil', precio: 12.56 },
  { base: 'ab broncol', precio: 18.07 },
  { base: 'doloral', precio: 9.63 },
  { base: 'doloral', precio: 17.22 },
  { base: 'uropol', precio: 165.04 },
  { base: 'pyridium', precio: 155.52 },
  { base: 'paldolor', precio: 109.96 },
  { base: 'magal', precio: 19.68 },
  { base: 'solutres', precio: 28.75 },
  { base: 'ak trol', precio: 13.73 },
]

// --- Grupo Farma Ecuador (id 3) ---
// Fuente: "LISTA DE PRECIOS AL 01072024" (GFE), columna PVF (precio de venta a
// fábrica/distribuidor). Cada presentación con precio distinto lleva su propia
// `base`. Las bases más específicas van primero (p.ej. "oralyte plus" antes que
// "oralyte").
const PRECIOS_GRUPOFARMA: PrecioBase[] = [
  // Medicamentos
  { base: 'aranda 2,5/50', precio: 26.75 },
  { base: 'aranda 5/100', precio: 36.33 },
  { base: 'bedoyecta', precio: 5.68 },
  { base: 'bioflora', precio: 14.08 },
  { base: 'brasartan ctdn 80/12,5', precio: 24.75 },
  { base: 'brasartan ctdn 80/25', precio: 31.25 },
  { base: 'brasartan ctdn 160/25', precio: 28.75 },
  { base: 'colypan plus 200/120', precio: 15.79 },
  { base: 'colypan sol iny', precio: 17.5 },
  { base: 'colypan 200mg', precio: 13.58 },
  { base: 'colypan 300mg', precio: 13.58 },
  { base: 'couplett tubo', precio: 14.58 },
  { base: 'couplett ovulos', precio: 16.19 },
  { base: 'diosmin', precio: 19.89 },
  { base: 'femen forte 400mg x 10', precio: 3.33 },
  { base: 'femen forte 400mg x 50', precio: 15.45 },
  { base: 'femen 200', precio: 3.05 },
  { base: 'ferinject', precio: 112.34 },
  { base: 'ferrum hausmann ampolla', precio: 8.08 },
  { base: 'ferrum hausmann gotas', precio: 8.33 },
  { base: 'ferrum hausmann jarabe', precio: 11.0 },
  { base: 'ferrum hausmann tablt', precio: 16.97 },
  { base: 'ferrum fol', precio: 16.74 },
  { base: 'fullderma', precio: 3.78 },
  { base: 'hidroten 12,5', precio: 12.5 },
  { base: 'hidroten 25', precio: 13.1 },
  { base: 'hepalive caps forte', precio: 18.5 },
  { base: 'hepalive caps advance', precio: 15.0 },
  { base: 'milpax susp. 150', precio: 4.0 },
  { base: 'milpax susp. 360', precio: 7.65 },
  { base: 'milpax nf', precio: 7.92 },
  { base: 'neolax polvo 17', precio: 15.92 },
  { base: 'neolax polvo 250', precio: 21.67 },
  { base: 'normix', precio: 13.03 },
  { base: 'notolac', precio: 8.33 },
  { base: 'oralyte plus', precio: 3.33 },
  { base: 'oralyte', precio: 6.0 },
  { base: 'pridot', precio: 21.67 },
  { base: 'prostum', precio: 24.92 },
  { base: 'senokot con docusato', precio: 7.29 },
  { base: 'senokot tab', precio: 5.3 },
  { base: 'stamyl', precio: 8.29 },
  { base: 'tapazol', precio: 10.83 },
  { base: 'urocit', precio: 31.76 },
  { base: 'venofer', precio: 14.26 },
  { base: 'vonau flash 4', precio: 12.08 },
  { base: 'vonau flash 8', precio: 15.29 },
  // Suplementos alimenticios
  { base: 'kidcal', precio: 15.4 },
  { base: 'farma d mag supl 2000', precio: 20.62 },
  { base: 'farma d mag supl 1000', precio: 15.7 },
  { base: 'farma d mag gotas', precio: 9.17 },
  { base: 'farma d mag 5000', precio: 12.13 },
  { base: 'calcibon natal', precio: 22.45 },
  { base: 'calcibon d soya', precio: 12.56 },
  { base: 'calcibon d 800 supl ui x 30', precio: 12.75 },
  { base: 'calcibon d 800 supl ui x 60', precio: 22.06 },
  { base: 'climasoy', precio: 25.0 },
  { base: 'hepalive sol', precio: 15.5 },
  { base: 'nutragesta', precio: 20.83 },
  { base: 'citramag', precio: 20.0 },
  { base: 'kolgel', precio: 39.17 },
  { base: 'cultiflor', precio: 14.86 },
]

const MAPA_MEDIFARMA = construirMapaPrecios(PRECIOS_MEDIFARMA)
const MAPA_MEDIFARMA_PERU = construirMapaPrecios(PRECIOS_MEDIFARMA_PERU)
const MAPA_GRUPOFARMA = construirMapaPrecios(PRECIOS_GRUPOFARMA)

/**
 * Devuelve el mapa de precios locales del reporte (`base → precio`), o `null` si
 * el reporte usa el `monto` del backend directamente. La detección de Medifarma
 * es por slug/nombre (varios reportes lo comparten); Grupo Farma se identifica
 * por id 3 o por slug/nombre.
 */
export function mapaPreciosReporte(
  cfg: { id?: number; slug?: string | null; nombre?: string | null } | null | undefined,
): Map<string, number> | null {
  const s = `${cfg?.slug ?? ''} ${cfg?.nombre ?? ''}`
  // Medifarma Perú (id 7) tiene su propia lista (soles); se evalúa ANTES del
  // match genérico de "medifarma" porque su nombre también contiene la marca.
  if (cfg?.id === 7) return MAPA_MEDIFARMA_PERU
  if (/medifarma/i.test(s)) return MAPA_MEDIFARMA
  if (cfg?.id === 3 || /grupo\s*farma|grupofarma/i.test(s)) return MAPA_GRUPOFARMA
  return null
}

/**
 * Devuelve el precio de referencia para un nombre de producto, matcheando por
 * prefijo de nombre base normalizado dentro del `mapa` dado. `null` si no hay
 * coincidencia (el llamador usa el `monto` del backend como respaldo).
 */
export function precioReferencia(
  mapa: Map<string, number>,
  nombre: string | null | undefined,
): number | null {
  if (!nombre) return null
  const clave = claveCadena(nombre)
  for (const [base, precio] of mapa) {
    if (clave.startsWith(base)) return precio
  }
  return null
}

function num(v: unknown): number {
  const n = typeof v === 'string' ? parseFloat(v) : typeof v === 'number' ? v : NaN
  return Number.isNaN(n) ? 0 : n
}

export interface ProductoVenta {
  nombre: string
  monto?: unknown
  cantidad_vendida?: unknown
}

/**
 * Valor $ de un producto a precio de referencia × cajas equivalentes. Si el
 * producto no tiene precio de referencia en `mapa`, se usa el `monto` del
 * backend (OCR).
 */
export function valorProductoReferencia(mapa: Map<string, number>, p: ProductoVenta): number {
  const precio = precioReferencia(mapa, p.nombre)
  return precio != null ? precio * num(p.cantidad_vendida) : num(p.monto)
}

/**
 * Precio de referencia promedio ponderado por cajas del conjunto de productos:
 * (Σ valor_referencia) / (Σ cajas). Sirve para valorar series temporales que
 * sólo traen cajas totales por día — al multiplicar las cajas de cada día por
 * este promedio, el total de la serie coincide con la valoración por producto.
 * Devuelve `null` si no hay cajas.
 */
export function precioReferenciaPromedioPonderado(
  mapa: Map<string, number>,
  productos: ProductoVenta[],
): number | null {
  let totalValor = 0
  let totalCajas = 0
  for (const p of productos) {
    totalCajas += num(p.cantidad_vendida)
    totalValor += valorProductoReferencia(mapa, p)
  }
  return totalCajas > 0 ? totalValor / totalCajas : null
}
